/**
 * Header inspector (spec D4) — the direct remap path. Edits produce a complete
 * spec that goes through PATCH /draft, so the server re-validates and re-runs
 * the full dry run. No LLM: whatever this panel cannot express deterministically
 * it refuses to offer, and points at the discuss dock instead.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Lock, MessageCircle, X } from 'lucide-react';
import Button from '../../components/ui/Button';
import type { InboundFileSpec } from '../../services/integrations-api';
import {
  applyRemap,
  FAMILY_ORDER,
  FAMILY_STYLE,
  offerableSlots,
  slotFamily,
  slotLabel,
  unofferedReason,
  type MappingFamily,
  type ReviewColumn,
} from './inboundFileMapping';

const POPOVER_WIDTH = 320;

interface Props {
  column: ReviewColumn;
  spec: InboundFileSpec;
  /** Header cell rect — the popover anchors under it. Ignored in compact mode. */
  anchor: DOMRect | null;
  compact: boolean;
  isSaving: boolean;
  /** Validator `details[]` from a 422, shown verbatim. */
  errors: string[];
  onApply: (next: InboundFileSpec) => void;
  onAskZamino: () => void;
  onClose: () => void;
}

/** Why the slot select is locked — null when the column is freely remappable. */
function lockReason(column: ReviewColumn): string | null {
  if (column.isTimestamp) {
    return 'Every total and comparison groups on this date, so it stays the time axis.';
  }
  if (column.isMaster) {
    return 'This is the master metric — which field leads the source is a chat request.';
  }
  if (column.siblings.length > 0) {
    return `This field combines it with ${column.siblings.join(', ')}.`;
  }
  return null;
}

/** Why "don't import" is unavailable even when remapping is fine. */
function unbindBlocker(column: ReviewColumn, spec: InboundFileSpec): string | null {
  if (!column.slot) return null;
  if (spec.naturalKey.includes(column.slot)) {
    return 'Row identity uses this field — change the identity first.';
  }
  if ((spec.rowRules ?? []).some((r) => r.slot === column.slot)) {
    return 'A skip rule reads this field — drop the rule first.';
  }
  return null;
}

const InboundFileColumnInspector: React.FC<Props> = ({
  column,
  spec,
  anchor,
  compact,
  isSaving,
  errors,
  onApply,
  onAskZamino,
  onClose,
}) => {
  const locked = lockReason(column);
  const unbindBlocked = unbindBlocker(column, spec);

  const [slot, setSlot] = useState<string | null>(column.slot);
  const [label, setLabel] = useState(column.binding?.friendlyLabel ?? column.header);
  const [description, setDescription] = useState(column.binding?.description ?? '');
  const [unit, setUnit] = useState(column.binding?.unit ?? '');
  const firstFieldRef = useRef<HTMLSelectElement>(null);

  useEffect(() => firstFieldRef.current?.focus(), []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const slotsByFamily = useMemo(() => {
    const grouped = new Map<MappingFamily, string[]>();
    for (const candidate of offerableSlots(column, spec)) {
      const family = slotFamily(candidate);
      if (family === 'skipped') continue;
      grouped.set(family, [...(grouped.get(family) ?? []), candidate]);
    }
    return grouped;
  }, [column, spec]);

  const narrowedTo = unofferedReason(column);
  const isMeasure = slotFamily(slot) === 'measure';
  const dirty =
    slot !== column.slot ||
    label.trim() !== (column.binding?.friendlyLabel ?? column.header) ||
    description.trim() !== (column.binding?.description ?? '') ||
    unit.trim() !== (column.binding?.unit ?? '');

  const positionStyle: React.CSSProperties =
    compact || !anchor
      ? {}
      : {
          position: 'fixed',
          width: POPOVER_WIDTH,
          left: Math.min(Math.max(8, anchor.left), window.innerWidth - POPOVER_WIDTH - 8),
          top: Math.min(anchor.bottom + 8, Math.max(8, window.innerHeight - 440)),
          maxHeight: window.innerHeight - 24,
        };

  const fieldClass =
    'w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground ' +
    'focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-60';

  return (
    <>
      {compact && (
        <div
          className="fixed inset-0 z-40 bg-foreground/20"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <div
        role="dialog"
        aria-label={`Column mapping for ${column.header}`}
        style={positionStyle}
        className={
          compact
            ? 'fixed inset-x-0 bottom-0 z-50 max-h-[85vh] overflow-y-auto rounded-t-xl border-t border-border bg-card p-4 shadow-lg'
            : 'z-50 overflow-y-auto rounded-xl border border-border bg-card p-3.5 shadow-lg'
        }
      >
        <div className="mb-2 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate font-mono text-xs font-semibold text-foreground">
              {column.header}
            </p>
            <p className="text-xs text-muted-foreground">
              {column.profile
                ? `${Math.round((1 - column.profile.nullRate) * 100)}% filled`
                : 'not profiled'}
              {column.worthALook && <span className="text-warn"> · worth a look</span>}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {!column.binding && column.skipReason && (
          <p className="mb-2.5 text-xs text-muted-foreground">{column.skipReason}</p>
        )}

        <div className="space-y-2.5">
          <div>
            <label htmlFor="inspector-slot" className="mb-1 block text-xs font-medium">
              Store as
            </label>
            <select
              id="inspector-slot"
              ref={firstFieldRef}
              value={slot ?? ''}
              disabled={locked !== null || isSaving}
              onChange={(e) => setSlot(e.target.value || null)}
              className={fieldClass}
            >
              <option value="" disabled={unbindBlocked !== null}>
                Don’t import this column
              </option>
              {FAMILY_ORDER.filter((family) => slotsByFamily.has(family)).map((family) => (
                <optgroup key={family} label={FAMILY_STYLE[family].label}>
                  {(slotsByFamily.get(family) ?? []).map((candidate) => (
                    <option key={candidate} value={candidate}>
                      {slotLabel(candidate)}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            {locked && (
              <p className="mt-1 flex items-start gap-1 text-xs text-muted-foreground">
                <Lock className="mt-0.5 h-3 w-3 shrink-0" />
                {locked}
              </p>
            )}
            {!locked && unbindBlocked && (
              <p className="mt-1 text-xs text-muted-foreground">{unbindBlocked}</p>
            )}
            {!locked && narrowedTo && (
              <p className="mt-1 text-xs text-muted-foreground">{narrowedTo}</p>
            )}
          </div>

          {slot !== null && (
            <>
              <div>
                <label htmlFor="inspector-label" className="mb-1 block text-xs font-medium">
                  Field name
                </label>
                <input
                  id="inspector-label"
                  type="text"
                  value={label}
                  disabled={isSaving}
                  onChange={(e) => setLabel(e.target.value)}
                  className={fieldClass}
                />
              </div>

              <div>
                <label htmlFor="inspector-description" className="mb-1 block text-xs font-medium">
                  Description
                </label>
                <textarea
                  id="inspector-description"
                  rows={2}
                  value={description}
                  disabled={isSaving}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What this field means, for whoever asks about it later"
                  className={`${fieldClass} resize-y`}
                />
              </div>

              {isMeasure && (
                <div>
                  <label htmlFor="inspector-unit" className="mb-1 block text-xs font-medium">
                    Unit
                  </label>
                  <input
                    id="inspector-unit"
                    type="text"
                    value={unit}
                    disabled={isSaving}
                    onChange={(e) => setUnit(e.target.value)}
                    placeholder={spec.currency ?? 'hours, units, …'}
                    className={fieldClass}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {errors.length > 0 && (
          <ul className="mt-2.5 space-y-0.5 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">
            {errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        )}

        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            size="sm"
            onClick={() =>
              onApply(applyRemap(spec, { column, slot, friendlyLabel: label, description, unit }))
            }
            disabled={!dirty || isSaving}
            isLoading={isSaving}
          >
            {isSaving ? 'Re-running dry run…' : 'Apply'}
          </Button>
          <Button size="sm" variant="ghost" onClick={onAskZamino} disabled={isSaving}>
            <MessageCircle className="mr-1.5 h-3.5 w-3.5" />
            Ask Zamino instead
          </Button>
        </div>
      </div>
    </>
  );
};

export default InboundFileColumnInspector;
